/**
 * Contrôles d'un document à lignes avant son enregistrement.
 *
 * <p>Le serveur exige une désignation sur chaque ligne d'un devis, d'une
 * facture ou d'un avenant. Une ligne laissée sans titre faisait refuser tout
 * l'enregistrement (« champs invalides ») : la synchronisation relisait alors
 * la base, et le document tout juste saisi disparaissait de l'écran. Les
 * éditeurs vérifient donc d'abord, et disent ce qui manque.
 */

/**
 * Dit si la ligne n'a pas de titre.
 *
 * @param {{d?: string}} ligne ligne du document
 * @returns {boolean} vrai si la désignation est vide ou faite d'espaces
 */
export function ligneSansTitre(ligne) {
  return !String(ligne?.d ?? '').trim();
}

/**
 * Rangs, comptés à partir de 1, des lignes sans titre.
 *
 * @param {Array<{d?: string}>} lignes lignes du document
 * @returns {number[]} les rangs à compléter, vide si toutes ont un titre
 */
export function rangsSansTitre(lignes) {
  return (lignes || []).map((l, i) => (ligneSansTitre(l) ? i + 1 : null)).filter(Boolean);
}

/**
 * Message qui dit quelles lignes n'ont pas de titre.
 *
 * @param {Array<{d?: string}>} lignes lignes du document
 * @param {string} [comment] geste qui complète la ligne
 * @returns {string|null} le message, ou {@code null} si toutes ont un titre
 */
export function messageTitreManquant(lignes, comment = 'touche la ligne puis remplis « Titre »') {
  const rangs = rangsSansTitre(lignes);
  if (rangs.length === 0) return null;
  return 'Donnez un titre à la ligne ' + rangs.join(', ') + ' : ' + comment + '.';
}

/**
 * Ce qui manque à un devis ou à une facture pour être enregistré.
 *
 * @param {object} doc document en cours de saisie
 * @param {*} doc.clientId client choisi
 * @param {Array<{d?: string}>} doc.lignes lignes saisies
 * @param {string} [destinataire] « le client », « le sous-traitant »…
 * @returns {string|null} le message à montrer, ou {@code null} s'il est complet
 */
export function ceQuiManqueDocument({ clientId, lignes }, destinataire = 'le client') {
  if (!clientId) {
    return 'Choisis d’abord ' + destinataire + ', en haut du formulaire.';
  }
  if (!lignes || lignes.length === 0) {
    return 'Ajoute au moins une ligne avec « ＋ ajouter un article ».';
  }
  return messageTitreManquant(lignes);
}

/**
 * Contrôle d'un titre modifié dans une ligne d'un document déjà enregistré.
 *
 * <p>Dans le détail d'une facture ou d'un devis, chaque frappe dans
 * « Désignation » est enregistrée aussitôt. Vider le champ envoyait une ligne
 * sans titre : le serveur refusait tout le document, la synchronisation
 * relisait la base et l'ancien titre revenait sans explication. Un titre vide
 * n'est donc jamais écrit, et ce message dit pourquoi.
 *
 * @param {string} valeur titre saisi
 * @param {number} rang rang de la ligne, compté à partir de 1
 * @returns {string|null} le message, ou {@code null} si le titre peut être enregistré
 */
export function messageTitreLigne(valeur, rang) {
  if (!ligneSansTitre({ d: valeur })) return null;
  return 'Donnez un titre à la ligne ' + rang + ' : la désignation ne peut pas rester vide.';
}
